import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import FilterTopHeader from '@features/auth/components/FilterTopHeader';
import MyColleagues from '@features/auth/components/MyColleagues';
import ProfileStrength from '@features/auth/components/ProfileStrength';

import ApiClient from '@lib/apiClient';

import { printApiError, validate } from '@utils/Util';

import { addressList, myProfilePageTabs } from '@/constants/constant';

import { getMailFrequency, setMailFrequency } from '@/services/commonService';

const MyProfile = () => {
	const [state, setState] = useState({
		loading: false,
		id: null,
		email: '',
		name: '',
		selectedAddress: {
			value: 'SINGAPORE OFFICE',
			id: '160 Robinson Road, #14-04, Singapore (068914)'
		},
		proPic: '',
		company: '',
		companyId: '',
		companyPrevious: '',
		designation: '',
		department: '',
		phone: '',
		countryId: null,
		address: '',
		linkedin: '',
		facebook: '',
		twitter: '',
		oldPassword: '',
		newPassword: '',
		newPasswordRe: '',
		oldPasswordError: '',
		newPasswordError: '',
		newPasswordReError: '',
		showSuggestion: false,
		suggestions: [],
		countryList: [],
		activeTab: 'PersonalInformation'
	});
	const navigate = useNavigate();
	const wrapperRef = useRef(null);

	useEffect(() => {
		document.title = 'My profile on Nitex - The easiest clothing manufacturing software';
		document.addEventListener('mousedown', handleClickOutside);
		fetchUserData();

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const getMailFrequencyValue = async () => {
			const mailFrequency = await getMailFrequency('MAIL_FREQUENCY');
			setState((prevState) => ({ ...prevState, emailSettings: mailFrequency.value }));
		};
		getMailFrequencyValue();
	}, []);

	const setWrapperRef = (node) => {
		wrapperRef.current = node;
	};

	const handleClickOutside = (event) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
			setState((prevState) => ({ ...prevState, showSuggestion: false }));
		}
	};

	const handleClickTabLi = (tabName) => {
		setState((prevState) => ({ ...prevState, activeTab: tabName }));
	};

	const fetchUserData = async () => {
		setState((prevState) => ({ ...prevState, loading: true }));
		try {
			const { data } = await ApiClient.GET('userInfo');
			if (data) {
				localStorage.setItem('userInfo', JSON.stringify(data));
				setState((prevState) => ({
					...prevState,
					id: data.id,
					loading: false,
					email: data.email || '',
					proPic: data.profilePicDocument?.docUrl || '',
					name: data.name || '',
					company: data.company?.name || '',
					companyId: data.company?.id || '',
					designation: data.designation || '',
					department: data.department || '',
					phone: data.phone || '',
					address: data.address || '',
					linkedin: data.linkedInUrl || '',
					facebook: data.facebookUrl || '',
					twitter: data.twitterUrl || ''
				}));
			} else {
				setState((prevState) => ({ ...prevState, loading: false }));
			}
		} catch (error) {
			setState((prevState) => ({ ...prevState, loading: false }));
			printApiError(error);
		}
	};

	const fetchSuggestions = async () => {
		try {
			const { data } = await ApiClient.GET('getCompanyList', '?name=' + state.company);
			setState((prevState) => ({
				...prevState,
				companyId: !data.length ? '' : prevState.companyId,
				suggestions: data
			}));
		} catch (error) {
			setState((prevState) => ({ ...prevState, loading: false }));
			printApiError(error);
		}
	};

	const onChangePass = (e) => {
		let err = validate(e, state.newPassword, true);
		setState((prevState) => ({
			...prevState,
			[e.target.name]: e.target.value,
			[err.name]: err.value
		}));
	};

	const onChange = (e) => {
		if (e.target.name === 'company') {
			fetchSuggestions();
			setState((prevState) => ({ ...prevState, companyId: '' }));
		}
		setState((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
	};

	const onFileUpload = (e, docType) => {
		let file = e.target.files[0];
		let data = {
			name: file.name,
			docMimeType: file.type,
			documentType: docType,
			base64Str: ''
		};
		let reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = async () => {
			data.base64Str = reader.result;
			setState((prevState) => ({ ...prevState, loading: true }));
			try {
				const { data: res } = await ApiClient.POST('updateProPic', data);
				if (res.success) {
					setState((prevState) => ({ ...prevState, loading: false }));
					toastSuccess(res.message);
					fetchDataAndReload();
				} else {
					setState((prevState) => ({ ...prevState, loading: false }));
					toastError(res.message);
				}
			} catch (error) {
				setState((prevState) => ({ ...prevState, loading: false }));
				printApiError(error);
			}
		};
	};

	const fetchDataAndReload = async () => {
		await fetchUserData();
		navigate(0);
	};

	const updateProfile = async () => {
		const {
			name,
			company,
			companyId,
			designation,
			department,
			phone,
			address,
			linkedin,
			facebook,
			twitter
		} = state;
		let body = {
			name,
			phone,
			address,
			designation,
			department,
			linkedInUrl: linkedin,
			facebookUrl: facebook,
			twitterUrl: twitter
		};
		if (companyId) body.companyId = companyId;
		else body.companyName = company;

		setState((prevState) => ({ ...prevState, loading: true }));
		try {
			const { data } = await ApiClient.POST('updateProfile', body);
			if (data.success) {
				setState((prevState) => ({ ...prevState, loading: false }));
				fetchDataAndReload();
				toastSuccess(data.message);
			} else {
				setState((prevState) => ({ ...prevState, loading: false }));
				toastError(data.message);
			}
		} catch (error) {
			setState((prevState) => ({ ...prevState, loading: false }));
			printApiError(error);
		}
	};

	const returnIfActive = (tabName) => {
		if (state.activeTab === tabName) return 'active';
		return 'fade';
	};

	const handleSelect = () => {
		const node = document.getElementById('select-table');
		if (document.body.createTextRange) {
			const range = document.body.createTextRange();
			range.moveToElementText(node);
			range.select();
		} else if (window.getSelection) {
			const selection = window.getSelection();
			const range = document.createRange();
			range.selectNodeContents(node);
			selection.removeAllRanges();
			selection.addRange(range);
		}
	};

	const setCompany = (item) => {
		setState((prev) => ({
			...prev,
			company: item.name,
			companyId: item.id,
			showSuggestion: false
		}));
	};

	const updatePassword = async () => {
		let {
			oldPassword,
			newPassword,
			newPasswordRe,
			oldPasswordError,
			newPasswordError,
			newPasswordReError
		} = state;
		if (!oldPasswordError && !newPasswordError && !newPasswordReError) {
			if (oldPassword && newPassword && newPasswordRe) {
				setState((prev) => ({ ...prev, loading: true }));
				let body = {
					currentPassword: oldPassword,
					password: newPassword,
					retypePassword: newPasswordRe
				};
				await ApiClient.POST('updatePassword', body)
					.then(({ data }) => {
						if (data.success) {
							toastSuccess(data.message);
						} else {
							toastError(data.message);
						}
						setState((prev) => ({ ...prev, loading: true }));
					})
					.catch((error) => {
						setState((prev) => ({ ...prev, loading: true }));
						printApiError(error);
					});
			} else {
				if (!oldPassword) {
					setState((prev) => ({ ...prev, oldPasswordError: 'Current password is required!' }));
				}
				if (!newPassword) {
					setState((prev) => ({ ...prev, newPasswordError: 'Password is required!' }));
				}
			}
		}
	};

	const updateEmailPreference = async () => {
		let { emailSettings } = state;

		if (emailSettings) {
			setState((prev) => ({ ...prev, loading: true }));
			await setMailFrequency(emailSettings);
			setState((prev) => ({ ...prev, loading: false }));
			toastSuccess('Email preference updated successfully');
		}
	};

	const onAddressClick = (obj) => {
		setState((prev) => ({ ...prev, selectedAddress: obj }));
	};

	const {
		name,
		email,
		designation,
		department,
		company,
		phone,
		address,
		linkedin,
		facebook,
		twitter,
		oldPassword,
		newPassword,
		newPasswordRe,
		oldPasswordError,
		newPasswordError,
		newPasswordReError,
		showSuggestion,
		suggestions,
		activeTab,
		selectedAddress
	} = state;

	return (
		<LoaderComponent loading={state.loading}>
			<div className="top-header bg-white">
				<div className="page-title-area bg-white">
					<h1 className="semibold-18">Profile</h1>
				</div>

				<section className="profile pb-0">
					<div className="profile-tab">
						<ul className="nav nav-tabs mb-0 border-0">
							{myProfilePageTabs.map((tab) => (
								<li key={tab.name} onClick={() => handleClickTabLi(tab.name)}>
									<a
										className={activeTab === tab.name ? 'active' : ''}
										data-toggle="tab"
										href={`#${tab.name}`}
									>
										{tab.label}
									</a>
								</li>
							))}
						</ul>
					</div>
					{state.activeTab === 'MyColleagues' && <FilterTopHeader />}
				</section>

				<section className="profile">
					<div className="profile-tab profile-information">
						<div className="tab-content mt-0">
							<div
								id="PersonalInformation"
								className={'tab-pane ' + returnIfActive('PersonalInformation')}
							>
								<div className="profile-info mt-5">
									<div className="user-photo">
										{state.proPic ? (
											<img src={state.proPic} alt="" />
										) : (
											<img
												src={`${process.env.PUBLIC_URL}/images/profile_avatar.png`}
												alt=""
											/>
										)}
										<div className="file btn">
											Change image
											<input
												type="file"
												name="proPic"
												onChange={(e) => onFileUpload(e, 'PROFILE_PHOTO')}
											/>
										</div>
									</div>
									<div className="user-info">
										<div className="row">
											<div className="col-lg-12 mb-2">
												<h3 className="semibold-16">Profile information</h3>
											</div>
											<div className="col-lg-12">
												<div className="form-group">
													<label htmlFor="name">Full name</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="John Doe"
														name="name"
														onChange={onChange}
														value={name}
													/>
												</div>
											</div>

											<div className="col-lg-12">
												<div className="form-group">
													<label htmlFor="email">Email</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="john.doe@gmail.com"
														name="email"
														onChange={onChange}
														value={email}
														disabled
													/>
												</div>
											</div>

											<div className="col-lg-6">
												<div className="form-group">
													<label htmlFor="designation">Designation</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="Marchandiser"
														name="designation"
														onChange={onChange}
														value={designation}
													/>
												</div>
											</div>

											<div className="col-lg-6">
												<div className="form-group">
													<label htmlFor="department">Department</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="Apparel engineering"
														name="department"
														onChange={onChange}
														value={department}
													/>
												</div>
											</div>

											<div className="col-lg-6">
												<div className="form-group">
													<label htmlFor="company">Company</label>
													<input
														type="text"
														placeholder="Nitex"
														name="company"
														onChange={onChange}
														value={company}
														onClick={() =>
															setState((prevState) => ({
																...prevState,
																showSuggestion: true
															}))
														}
														autoComplete="off"
													/>
													{showSuggestion && (
														<div className="suggest-filed" ref={setWrapperRef}>
															<ul>
																{suggestions.length ? (
																	suggestions.map((item) => (
																		<li
																			key={item.id}
																			onClick={() => setCompany(item)}
																		>
																			{item.name}
																		</li>
																	))
																) : (
																	<li
																		onClick={() =>
																			setState((prevState) => ({
																				...prevState,
																				showSuggestion: false
																			}))
																		}
																	>
																		No suggestions found...
																	</li>
																)}
															</ul>
														</div>
													)}
												</div>
											</div>

											<div className="col-lg-6">
												<div className="form-group">
													<label htmlFor="phone">Mobile number</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="+88017..."
														name="phone"
														onChange={onChange}
														value={phone}
													/>
												</div>
											</div>

											<div className="col-lg-12">
												<div className="form-group mb-0">
													<label>Address</label>
													<textarea
														cols="30"
														rows="3"
														name="address"
														value={address}
														onChange={onChange}
														placeholder="Enter address"
													/>
												</div>
											</div>
										</div>

										<div className="row">
											<div className="col-lg-12 mb-2">
												<h3 className="semibold-16">Social information</h3>
											</div>
											<div className="col-lg-12">
												<div className="form-group">
													<label htmlFor="linkedin">LinkedIn profile</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="https://www.linkedin.com/in/"
														name="linkedin"
														onChange={onChange}
														value={linkedin}
													/>
												</div>
											</div>
											<div className="col-lg-12">
												<div className="form-group">
													<label htmlFor="facebook">Facebook profile</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="https://www.facebook.com/"
														name="facebook"
														onChange={onChange}
														value={facebook}
													/>
												</div>
											</div>
											<div className="col-lg-12">
												<div className="form-group mb-0">
													<label htmlFor="twitter">Twitter profile</label>
													<input
														type="text"
														autoComplete="off"
														placeholder="https://www.twitter.com/"
														name="twitter"
														onChange={onChange}
														value={twitter}
													/>
												</div>
											</div>
										</div>

										<div className="row row-submit">
											<div className="col-lg-12 text-right px-0">
												<button className="button" onClick={updateProfile}>
													Save changes
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div
								id="ChangePassword"
								className={'tab-pane ' + returnIfActive('ChangePassword')}
							>
								<div className="change-pass mt-5">
									<div className="row">
										<div className="col-lg-12 mb-2">
											<h3 className="semibold-16">Password information</h3>
										</div>
										<div className="col-lg-12">
											<div className="form-group">
												<label htmlFor="oldPassword">Current password</label>
												<input
													type="password"
													placeholder="************"
													name="oldPassword"
													onChange={onChangePass}
													value={oldPassword}
												/>
												{oldPasswordError && (
													<span className="error-text">{oldPasswordError}</span>
												)}
											</div>
										</div>

										<div className="col-lg-12">
											<div className="form-group">
												<label htmlFor="newPassword">New password</label>
												<input
													type="password"
													placeholder="************"
													name="newPassword"
													onChange={onChangePass}
													value={newPassword}
												/>
												{newPasswordError && (
													<span className="error-text">{newPasswordError}</span>
												)}
											</div>
										</div>

										<div className="col-lg-12">
											<div className="form-group mb-0">
												<label htmlFor="newPasswordRe">Confirm new password</label>
												<input
													type="password"
													placeholder="************"
													name="newPasswordRe"
													onChange={onChangePass}
													value={newPasswordRe}
												/>
												{newPasswordReError && (
													<span className="error-text">{newPasswordReError}</span>
												)}
											</div>
										</div>
									</div>

									<div className="row row-submit">
										<div className="col-lg-12 text-right px-0">
											<button className="button" onClick={updatePassword}>
												Update password
											</button>
										</div>
									</div>
								</div>
							</div>

							<div
								id="EmailPreferences"
								className={'tab-pane ' + returnIfActive('EmailPreferences')}
							>
								<div className="email-preferences mt-5">
									<div className="row">
										<div className="col-lg-12 mb-2">
											<h3 className="semibold-16">Project emails</h3>
										</div>
										<div className="col-lg-12">
											<div className="custom-radio">
												<div className="form-group">
													<input
														type="radio"
														id="card"
														value="NONE"
														name="emailSettings"
														checked={state.emailSettings === 'NONE'}
														onChange={onChange}
													/>
													<label htmlFor="card">Don&#39;t send any emails</label>
												</div>
											</div>

											<div className="custom-radio">
												<div className="form-group">
													<input
														type="radio"
														id="card1"
														value="LIMITED"
														name="emailSettings"
														checked={state.emailSettings === 'LIMITED'}
														onChange={onChange}
													/>
													<label htmlFor="card1">Email important updates</label>
												</div>
											</div>

											<div className="custom-radio">
												<div className="form-group">
													<input
														type="radio"
														id="card2"
														value="ALL"
														name="emailSettings"
														checked={state.emailSettings === 'ALL'}
														onChange={onChange}
													/>
													<label htmlFor="card2">Email on all updates</label>
												</div>
											</div>
										</div>
									</div>
									<div className="row row-submit">
										<div className="col-lg-12 text-right px-0">
											<button className="button" onClick={updateEmailPreference}>
												Save changes
											</button>
										</div>
									</div>
								</div>
							</div>

							<div id="Signature" className={'tab-pane ' + returnIfActive('Signature')}>
								<section
									className="signature-section mt-5"
									style={{ fontSize: '10pt', fontFamily: '"Open Sans", sans-serif' }}
								>
									<div className="mb-3">
										<label>Select office address</label>
										<CustomDropdown
											type="addItem"
											title={'Select Address'}
											onItemClick={onAddressClick}
											items={addressList}
											selectedItem={selectedAddress?.value}
											onItemSearch={() => false}
											isShowSearch={false}
										/>
									</div>
									{/* New version Signature */}
									<table
										id="select-table"
										style={{
											width: 440,
											fontSize: '10pt',
											fontFamily: 'Verdana, sans-serif',
											background: '#ffffff',
											border: '1px solid #EDF0F2'
										}}
										cellPadding={0}
										cellSpacing={0}
										border={0}
									>
										<tbody>
											<tr>
												<td
													width={110}
													align="center"
													style={{
														fontSize: '10pt',
														fontFamily: '"Open Sans", sans-serif',
														width: 110,
														textAlign: 'center',
														verticalAlign: 'top'
													}}
													valign="top"
													rowSpan={6}
												>
													<p
														style={{
															marginBottom: 10,
															marginTop: 12,
															marginLeft: 12
														}}
													>
														<img
															src={
																state.proPic
																	? state.proPic
																	: `${process.env.PUBLIC_URL}/images/profile_avatar.png`
															}
															alt="photograph"
															style={{
																height: '108px',
																width: '110px',
																objectFit: 'cover',
																objectPosition: 'top'
															}}
															width="108"
															height="110"
														/>
													</p>
													<img
														src={`${process.env.PUBLIC_URL}/images/logo-signature.png`}
														alt="logo"
														style={{
															width: '108px',
															height: '18px',
															objectFit: 'contain',
															marginLeft: 10,
															marginTop: 4,
															marginBottom: 12
														}}
														width="108"
														height="18"
													/>
												</td>
												<td width={20} style={{ width: 14 }}>
													&nbsp;
												</td>
												<td>
													<table cellPadding={0} cellSpacing={0}>
														<tbody>
															<tr>
																<td
																	style={{
																		fontSize: '10pt',
																		color: '#0079ac',
																		fontFamily: '"Open Sans", sans-serif',
																		width: 270,
																		paddingTop: 10,
																		verticalAlign: 'top',
																		lineHeight: '1.2'
																	}}
																	valign="top"
																>
																	<strong>
																		<span
																			style={{
																				fontSize: '11pt',
																				fontFamily:
																					'"Open Sans", sans-serif',
																				color: '#000000'
																			}}
																		>
																			{name}
																			<br />
																		</span>
																	</strong>
																	<em
																		style={{
																			fontFamily:
																				'"Open Sans", sans-serif',
																			fontSize: '9pt',
																			fontWeight: 700,
																			color: '#6A7181',
																			paddingTop: 2,
																			display: 'inline-block',
																			fontStyle: 'normal'
																		}}
																	>
																		{designation} |
																		<a
																			style={{
																				fontSize: '8pt',
																				color: '#000000',
																				textDecoration: 'underline',
																				fontStyle: 'normal',
																				marginLeft: 4
																			}}
																			href={linkedin}
																		>
																			LinkedIn
																		</a>
																	</em>
																</td>
															</tr>
															<tr>
																<td
																	style={{
																		fontSize: '9pt',
																		color: '#444444',
																		fontFamily: '"Open Sans", sans-serif',
																		paddingTop: 8,
																		paddingBottom: 0,
																		verticalAlign: 'top',
																		lineHeight: '1.2'
																	}}
																	valign="top"
																>
																	<table>
																		<tr>
																			<td>
																				<p
																					style={{
																						fontSize: '10pt',
																						fontFamily:
																							'"Open Sans", sans-serif',
																						color: '#6A7181'
																					}}
																				>
																					{phone}
																				</p>
																				<p
																					style={{
																						fontSize: '10pt',
																						fontFamily:
																							'"Open Sans", sans-serif',
																						color: '#6A7181'
																					}}
																				>
																					{email}
																				</p>
																				<p
																					style={{
																						fontSize: '10pt',
																						fontFamily:
																							'"Open Sans", sans-serif',
																						color: '#6A7181',
																						marginTop: 8
																					}}
																				>
																					{selectedAddress?.id}
																				</p>
																				<a
																					href="https://www.nitex.com/"
																					target="_blank"
																					rel="noopener noreferrer"
																					style={{
																						textDecoration:
																							'none',
																						fontSize: '10pt',
																						fontFamily:
																							'"Open Sans", sans-serif',
																						color: '#000000',
																						fontWeight: 700,
																						marginTop: 12,
																						marginBottom: 8,
																						display:
																							'inline-block'
																					}}
																				>
																					<span>www.nitex.com</span>
																				</a>
																			</td>
																		</tr>
																	</table>
																</td>
															</tr>
														</tbody>
													</table>
												</td>
											</tr>
										</tbody>
									</table>

									<div className="select-signature-btn text-right mt-2">
										<button className="button size32" onClick={handleSelect}>
											Select
										</button>
										<p className="regular-14 gray_dark_02 mt-2">
											To use this signature, click the &#34;Select&#34; button & copy
											it, then paste it in your email signature box.
										</p>
									</div>
								</section>
							</div>
							{activeTab === 'Strength' && (
								<div id="Strength" className={'tab-pane ' + returnIfActive('Strength')}>
									<ProfileStrength id={state.id} /> {/* Example */}
								</div>
							)}
							{activeTab === 'MyColleagues' && (
								<div
									id="MyColleagues"
									className={'tab-pane ' + returnIfActive('MyColleagues')}
								>
									<MyColleagues /> {/* Example */}
								</div>
							)}
						</div>
					</div>
				</section>
			</div>
		</LoaderComponent>
	);
};

export default MyProfile;
